import { useEffect, useRef, useState } from 'react';
import { CatalogTable } from '@backstage/plugin-catalog';
import { featureFlagsApiRef, useApi } from '@backstage/frontend-plugin-api';
import {
  CatalogFilterLayout,
  EntityKindPicker,
  EntityLifecyclePicker,
  EntityListProvider,
  EntityOwnerPicker,
  EntityOwnerPickerProps,
  EntityProcessingStatusPicker,
  EntityTagPicker,
  EntityTypePicker,
  UserListFilterKind,
  UserListPicker,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { Container } from '@backstage/ui';
import { Page } from '@internal/backstage-plugin-hm-react';

export { RiShapesFill as CatalogLandingPageIcon } from '@remixicon/react';

type CatalogLandingPageProps = {
  initiallySelectedFilter?: UserListFilterKind;
  initialKind?: string;
  ownerPickerMode?: EntityOwnerPickerProps['mode'];
};

type CatalogUserListPickerProps = {
  initialFilter: UserListFilterKind;
  resetToOwnedOnComponentReturn: boolean;
};

const CatalogUserListPicker = ({
  initialFilter,
  resetToOwnedOnComponentReturn,
}: CatalogUserListPickerProps) => {
  const { filters, updateFilters } = useEntityList();
  const selectedKind = filters.kind?.value?.toLowerCase();
  const previousKind = useRef(selectedKind);
  const [resetVersion, setResetVersion] = useState(0);

  useEffect(() => {
    const returnedToComponents =
      previousKind.current !== 'component' && selectedKind === 'component';
    previousKind.current = selectedKind;

    if (resetToOwnedOnComponentReturn && returnedToComponents) {
      updateFilters({ user: undefined });
      setResetVersion(version => version + 1);
    }
  }, [resetToOwnedOnComponentReturn, selectedKind, updateFilters]);

  return (
    <UserListPicker
      key={`${selectedKind ?? 'all'}-${resetVersion}`}
      initialFilter={initialFilter}
    />
  );
};

export const CatalogLandingPage = ({
  initiallySelectedFilter,
  initialKind = 'component',
  ownerPickerMode,
}: CatalogLandingPageProps) => {
  const ownedFilterEnabled = useApi(featureFlagsApiRef).isActive(
    'catalog-owned-filter',
  );
  const selectedFilter =
    initiallySelectedFilter ?? (ownedFilterEnabled ? 'owned' : 'all');

  return (
    <Page title="H&M Catalog">
      <EntityListProvider>
        <Container>
          <CatalogFilterLayout>
            <CatalogFilterLayout.Filters options={{ drawerAnchor: 'right' }}>
              <EntityKindPicker initialFilter={initialKind} />
              <EntityTypePicker />
              <CatalogUserListPicker
                initialFilter={selectedFilter}
                resetToOwnedOnComponentReturn={
                  ownedFilterEnabled && initiallySelectedFilter === undefined
                }
              />
              <EntityOwnerPicker mode={ownerPickerMode} />
              <EntityLifecyclePicker />
              <EntityTagPicker />
              <EntityProcessingStatusPicker />
            </CatalogFilterLayout.Filters>

            <CatalogFilterLayout.Content>
              <CatalogTable />
            </CatalogFilterLayout.Content>
          </CatalogFilterLayout>
        </Container>
      </EntityListProvider>
    </Page>
  );
};
